import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Check, Footprints, Timer, ChevronRight, ChevronLeft, Dumbbell, Lock, Play, Repeat, Flame, TrendingUp, Zap, ArrowLeftRight, Pencil, RotateCcw, Trash2, Volume2 } from 'lucide-react';
import { RecoveryTimer } from '@/components/RecoveryTimer';
import { format, parseISO, addDays, differenceInCalendarDays } from 'date-fns';
import { SessionSwapDialog, AddSessionDialog } from '@/components/SessionSwapDialog';
import { useProgramLocked, LockedScreen } from '@/components/ProgramLock';
import { ExerciseMedia, optimized360 } from '@/components/ExerciseMedia';
import { ActivityLogger } from '@/components/ActivityLogger';
import { ProgramHeader } from '@/components/ProgramHeader';
import { ContinueProgramCard } from '@/components/ContinueProgramCard';
import { SessionEditor, loadExerciseMap } from '@/components/SessionEditor';
import { AddWorkoutPicker } from '@/components/AddWorkoutPicker';
import { GuidedWorkout } from '@/components/guided/GuidedWorkout';
import { resolveTempo } from '@/lib/coachScript';

export default function Training() {
  const locked = useProgramLocked();
  const { user } = useAuth();
  const nav = useNavigate();
  const isTracker = (user?.subscription_tier || '') === 'tracker';
  const [assignments, setAssignments] = useState([]);
  const [tab, setTab] = useState('today');
  const [progStart, setProgStart] = useState(null);
  const [weekShift, setWeekShift] = useState(0);
  const loggerRef = useRef(null);
  const goOther = () => { setTab('today'); setTimeout(() => loggerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80); };
  const today = format(new Date(), 'yyyy-MM-dd');

  const load = async () => {
    const r = await api.get('/workouts/assignments');
    const seen = new Set();
    setAssignments((r.data || []).filter(a => !seen.has(a.id) && seen.add(a.id)).sort((x, y) => x.date.localeCompare(y.date)));
  };
  useEffect(() => {
    if (locked || isTracker) return;
    load();
    api.get('/my-programs').then(r => setProgStart(((r.data || []).find(p => p.program_type === 'training') || {}).start_date || null)).catch(() => {});
  }, []);

  const todayList = assignments.filter(a => a.date === today);
  const wk = weekRange(today, progStart, weekShift);
  const weekList = assignments.filter(a => a.date >= wk.start && a.date <= wk.end);
  const history = assignments.filter(a => a.date < today || (a.date === today && a.status === 'completed')).reverse();
  const hasHiit = assignments.some(a => (a.workout_snapshot?.workout_type) === 'HIIT');
  const hasFr = assignments.some(a => (a.workout_snapshot?.workout_type) === 'Fitness Racing');

  if (locked) return <LockedScreen title="Training" color="#00C8FF" />;

  // Free YourReGen tracker → manual workout logging only (personalized programs are YourReGen+).
  if (isTracker) {
    return (
      <div className="space-y-4 page-grid" data-testid="client-training">
        <div>
          <div className="label-mini text-[#00C8FF]">Log Your Training</div>
          <h1 className="font-display font-black text-3xl mt-1">Training</h1>
          <p className="text-sm text-muted-foreground mt-1">Log any workout or activity and track your history.</p>
        </div>
        <button onClick={() => nav('/choose-journey')} data-testid="training-program-upsell"
          className="w-full text-left regen-card-elevated p-4 flex items-center gap-3 active:scale-[0.99] transition border border-[#00C8FF]/20">
          <div className="w-11 h-11 rounded-xl bg-[#00C8FF]/15 flex items-center justify-center flex-shrink-0 relative">
            <Dumbbell className="w-5 h-5 text-[#00C8FF]" />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--c-elev2)] border border-[var(--c-hair)] flex items-center justify-center"><Lock className="w-2.5 h-2.5 text-[#00C8FF]" /></span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Personalized Workout Program</div>
            <div className="text-xs text-muted-foreground">Get a plan built around your goals — start your 7-day YourReGen+ free trial.</div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>
        <ActivityLogger title="Log a Workout" subtitle="Search your workout or activity, add the duration, and track your history." showBonusTag={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4 page-grid" data-testid="client-training">
      <div>
        <div className="label-mini text-[#00C8FF]">Pillar 2</div>
        <h1 className="font-display font-black text-3xl mt-1">Training</h1>
      </div>

      <ProgramHeader type="training" />

      {(hasHiit || hasFr) && <CardioLevels hasHiit={hasHiit} hasFr={hasFr} />}

      <ContinueProgramCard />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-regen-card border border-[var(--c-hair)]">
          <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week">Week</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4 space-y-4">
          {todayList.length === 0 ? <EmptyState text="No workout scheduled today. Rest day or message your coach." /> : todayList.map(a => <WorkoutCard key={a.id} assignment={a} onUpdate={load} />)}
          <AddWorkoutPicker onOther={goOther} onSaved={load} defaultDate={today} />
          <div ref={loggerRef}><ActivityLogger /></div>
        </TabsContent>
        <TabsContent value="week" className="mt-4 space-y-4">
          <WeekNav wk={wk} onPrev={() => setWeekShift(s => s - 1)} onNext={() => setWeekShift(s => s + 1)} />
          {weekList.length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="week-total">
              {weekList.length} session{weekList.length === 1 ? '' : 's'} this week
              {weekList.some(a => a.extra) && ` · ${weekList.filter(a => !a.extra).length} planned + ${weekList.filter(a => a.extra).length} extra`}
              {' · '}{weekList.filter(a => a.status === 'completed').length} done
            </div>
          )}
          {wk.end >= today && <AddWorkoutPicker onOther={goOther} onSaved={load} defaultDate={wk.start > today ? wk.start : today} />}
          {progStart && wk.end >= today && <AddSessionButton wk={wk} today={today} busy={new Set(assignments.map(a => a.date))} onDone={load} hasFr={hasFr} />}
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start">
            {weekList.length === 0 ? <div className="lg:col-span-2"><EmptyState text="No sessions scheduled this week." /></div> : weekList.map(a => <WorkoutCard key={a.id} assignment={a} onUpdate={load} readOnly={a.date < today} />)}
          </div>
        </TabsContent>
        <TabsContent value="history" className="mt-4 space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start">
          {history.length === 0 ? <div className="lg:col-span-2"><EmptyState text="No past workouts yet." /></div> : history.map(a => <WorkoutCard key={a.id} assignment={a} readOnly onUpdate={load} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const iso = (d) => format(d, 'yyyy-MM-dd');
function weekRange(today, progStart, shift) {
  const t = parseISO(today);
  let start, label = null;
  if (progStart) {
    const ps = parseISO(progStart);
    const idx = Math.floor(differenceInCalendarDays(t, ps) / 7) + shift;
    start = addDays(ps, idx * 7);
    if (idx >= 0) label = `Programme Week ${idx + 1}`;
  } else {
    start = addDays(t, -((t.getDay() + 6) % 7) + shift * 7);
  }
  const end = addDays(start, 6);
  return { start: iso(start), end: iso(end), label, text: `${format(start, 'MMM d')} – ${format(end, 'MMM d')}` };
}

function AddSessionButton({ wk, today, busy, onDone, hasFr }) {
  const [open, setOpen] = useState(false);
  const free = [];
  for (let d = parseISO(wk.start); iso(d) <= wk.end; d = addDays(d, 1)) {
    if (iso(d) >= today && !busy.has(iso(d))) free.push(iso(d));
  }
  if (!free.length) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="add-extra-session" className="w-full border-dashed border-[var(--c-hair)]">
        + Add an extra session this week
      </Button>
      <AddSessionDialog open={open} onOpenChange={setOpen} freeDates={free} onDone={onDone} hasFr={hasFr} />
    </>
  );
}

function WeekNav({ wk, onPrev, onNext }) {
  return (
    <div className="flex items-center justify-between regen-card p-2" data-testid="week-nav">
      <button onClick={onPrev} data-testid="week-prev" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--c-film)]"><ChevronLeft className="w-4 h-4" /></button>
      <div className="text-center">
        {wk.label && <div className="label-mini text-[#00C8FF]" data-testid="week-label">{wk.label}</div>}
        <div className="text-sm font-mono" data-testid="week-range">{wk.text}</div>
      </div>
      <button onClick={onNext} data-testid="week-next" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--c-film)]"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

const LEVELS = [{ n: 1, label: 'Foundation' }, { n: 2, label: 'Performance' }, { n: 3, label: 'Advanced' }, { n: 4, label: 'Elite' }];

function CardioLevels({ hasHiit, hasFr }) {
  return (
    <div className={`grid gap-3 ${hasHiit && hasFr ? 'grid-cols-2' : 'grid-cols-1 lg:grid-cols-2'}`} data-testid="cardio-levels">
      {hasHiit && <LevelCard endpoint="/hiit/level" title="HIIT" color="#FF6B35" icon={Flame} prefix="hiit" />}
      {hasFr && <LevelCard endpoint="/fitness-racing/level" title="Fitness Racing" color="#65A30D" icon={Zap} prefix="fr" />}
    </div>
  );
}

function LevelCard({ endpoint, title, color, icon: Icon, prefix }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => { try { const r = await api.get(endpoint); setData(r.data); } catch { /* no-op */ } };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="regen-card-elevated p-3 min-h-[140px]" />;
  const choose = async (n) => {
    if (n === data.level) return;
    setSaving(true);
    try {
      const r = await api.put(endpoint, { level: n });
      toast.success(`${title} level set to Level ${n} — ${r.data.label}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update level'); }
    finally { setSaving(false); }
  };
  return (
    <div className="regen-card-elevated p-3 sm:p-4 flex flex-col gap-2 min-w-0" data-testid={`${prefix}-level-card`}>
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}26` }}><Icon className="w-4 h-4" style={{ color }} /></div>
        <div className="min-w-0">
          <div className="font-display font-bold text-sm sm:text-base leading-tight truncate">{title}</div>
          <div className="text-[11px] text-muted-foreground">Training selection</div>
        </div>
      </div>
      <div className="h-px bg-[var(--c-hair)]" />
      <div className="text-xs font-medium"><span className="sm:hidden">Select level</span><span className="hidden sm:inline">Select {title} Level</span></div>
      <div className="grid grid-cols-4 gap-1" role="radiogroup">
        {LEVELS.map(l => (
          <button key={l.n} disabled={saving} onClick={() => choose(l.n)} data-testid={`${prefix}-level-option-${l.n}`} title={l.label}
            role="radio" aria-checked={data.level === l.n}
            className="h-8 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50 hover:bg-[var(--c-film)]"
            style={data.level === l.n ? { borderColor: color, backgroundColor: `${color}26`, color } : { borderColor: 'var(--c-hair)' }}>
            {l.n}
          </button>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground truncate" data-testid={`${prefix}-level-current`}>Level {data.level} — {data.label}</div>
      {data.recommendation && (
        <div className="p-2 rounded-lg bg-[#00C8FF]/5 border border-[#00C8FF]/20 flex gap-1.5" data-testid={`${prefix}-recommendation`}>
          <TrendingUp className="w-3.5 h-3.5 text-[#00C8FF] shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-snug line-clamp-3 sm:line-clamp-none" title={data.recommendation}>{data.recommendation}</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="regen-card-elevated p-8 text-center text-muted-foreground">{text}</div>;
}

const todayIso = () => format(new Date(), 'yyyy-MM-dd');
// Estimated durations were removed from programmes; hide them on older sessions too.
const stripDuration = (d) => (d || '').replace(/\s*·?\s*\d+\s*[-–]\s*\d+\s*min(utes)?\b/gi, '').trim();


function WorkoutCard({ assignment, onUpdate, readOnly }) {
  const w = assignment.custom_snapshot || assignment.workout_snapshot || {};
  const [editOpen, setEditOpen] = useState(false);
  const isCustom = !!assignment.custom_snapshot;
  const [completed, setCompleted] = useState(new Set(assignment.completed_exercise_ids || []));
  useEffect(() => { setCompleted(new Set(assignment.completed_exercise_ids || [])); }, [assignment.completed_exercise_ids]);
  const [steps, setSteps] = useState(assignment.steps || 0);
  const [detailEx, setDetailEx] = useState(null);
  const [exMap, setExMap] = useState({});
  const isWalking = w.workout_type === 'Walking';
  // Treat HIIT / Circuit-Training workouts as guided circuits (auto recovery timers + per-occurrence
  // state) even if not explicitly migrated. Rounds default to 1 (no guessing — 1 round = the listed
  // sequence performed once; the admin migration tool sets a higher count when the plan calls for it).
  const isCircuit = (w.workout_format === 'Circuit' || w.workout_type === 'HIIT' || w.workout_type === 'Circuit Training') && (w.items || []).length > 0 && !readOnly;
  const rounds = Math.max(1, w.rounds || 1);
  const [circuitOpen, setCircuitOpen] = useState(false);
  const [restTimer, setRestTimer] = useState(null); // {seconds, nextLabel} — strength recovery overlay
  const [subs, setSubs] = useState({});             // session-only exercise substitutions: idx -> exercise_id
  const [actuals, setActuals] = useState({});       // what the client actually did: idx -> {reps_done, weight_used}
  const [subPickIdx, setSubPickIdx] = useState(null); // which occurrence's "Change Exercise" dialog is open
  // Substitutions are Strength-only, temporary (this session), and keep the original sets/reps/rest/tempo.
  const canSub = w.workout_type === 'Strength Training' && !isCircuit && !isWalking && !readOnly;
  const [swapOpen, setSwapOpen] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [raceOpen, setRaceOpen] = useState(false);
  const isRace = w.workout_type === 'Fitness Racing' && (w.items || []).length > 0 && !readOnly && assignment.status !== 'completed';
  const isStrength = w.workout_type === 'Strength Training' && !isCircuit && !isWalking;
  const canGuide = isStrength && !readOnly && assignment.status !== 'completed' && (w.items || []).length > 0;
  const canSwap = !readOnly && !assignment.custom_built && assignment.status !== 'completed' && assignment.date >= todayIso()
    && ['Strength Training', 'HIIT', 'Fitness Racing'].includes(w.workout_type);
  const resolvedEx = (idx, it) => exMap[subs[idx] || it.exercise_id];

  // Fetch the exercise library ONCE per card (was previously fetched per-row).
  useEffect(() => {
    loadExerciseMap().then(setExMap).catch(() => {});
  }, []);

  // Preload the NEXT exercise's 360p clip so tapping through is instant.
  const items = w.items || [];
  const curIdx = detailEx ? items.findIndex(it => it.exercise_id === detailEx.item.exercise_id) : -1;
  const nextSrc = curIdx >= 0 ? exMap[items[curIdx + 1]?.exercise_id]?.gif_url : null;

  const toggle = (idx, item) => {
    if (readOnly) return;
    const key = String(idx);
    const next = new Set(completed);
    const wasDone = next.has(key);
    if (wasDone) next.delete(key); else next.add(key);
    setCompleted(next);
    // General recovery-timer feature: marking an exercise DONE in a non-circuit workout that has a
    // programmed rest auto-starts the same recovery countdown used by circuits (reusable component).
    if (!wasDone && !isCircuit && (item?.rest_seconds || 0) > 0) {
      const nextItem = (w.items || [])[idx + 1];
      const nextName = nextItem ? (exMap[nextItem.exercise_id]?.name || nextItem.exercise_name || 'Next exercise') : null;
      setRestTimer({ seconds: item.rest_seconds, nextLabel: nextName });
    }
  };

  const complete = async () => {
    try {
      await api.post('/workouts/complete', {
        assignment_id: assignment.id,
        completed_exercise_ids: Array.from(completed),
        steps: isWalking ? parseInt(steps) || 0 : null,
        performed_items: isWalking ? null : items.map((it, i) => ({ ...it, ...(actuals[i] || {}), exercise_id: subs[i] || it.exercise_id, done: completed.has(String(i)) })),
      });
      toast.success('Workout completed');
      onUpdate?.();
    } catch (err) {
      toast.error('Failed to complete');
    }
  };

  return (
    <div className="regen-card-elevated p-5" data-testid={`workout-${assignment.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mini">{w.workout_type}{isCircuit ? ' · Circuit' : ''}{assignment.extra && <span className="ml-2 pill bg-[#00C8FF]/15 text-[#00C8FF] normal-case" data-testid={`extra-tag-${assignment.id}`}>{assignment.custom_built ? 'Extra · built by you' : 'Extra session'}</span>}{isCustom && <span className="ml-2 pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] normal-case" data-testid={`custom-tag-${assignment.id}`}>Customised</span>}</div>
          <div className="font-display font-bold text-xl mt-1">{w.name}</div>
          <div className="text-xs text-muted-foreground mt-1">{format(parseISO(assignment.date), 'EEE, MMM d')}</div>
        </div>
        {assignment.status === 'completed'
          ? <div className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)]" data-testid={`status-done-${assignment.id}`}><Check className="w-3 h-3 mr-1" /> Done{assignment.guided ? ` · Voice-guided${assignment.duration_seconds ? ` · ${Math.max(1, Math.round(assignment.duration_seconds / 60))} min` : ''}` : ''}</div>
          : assignment.date < todayIso() && <div className="pill bg-[#FF5C5C]/15 text-[#FF5C5C]" data-testid={`status-missed-${assignment.id}`}>Missed</div>}
        {canSwap && (
          <Button size="sm" variant="outline" onClick={() => setSwapOpen(true)} data-testid={`swap-session-${assignment.id}`} className="border-[var(--c-hair)] shrink-0">
            <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Swap
          </Button>
        )}
      </div>
      {canSwap && <SessionSwapDialog assignment={assignment} open={swapOpen} onOpenChange={setSwapOpen} onDone={onUpdate} />}
      {!isWalking && <SessionTools assignment={assignment} isCustom={isCustom} onEdit={() => setEditOpen(true)} onUpdate={onUpdate} />}
      {!isWalking && <SessionEditor open={editOpen} onOpenChange={setEditOpen} assignment={assignment} onSaved={onUpdate} />}
      {w.client_notes && (
        <div className="mt-3 rounded-xl bg-[var(--c-film)] border border-[var(--c-hair)] p-3" data-testid={`client-notes-${assignment.id}`}>
          <div className="label-mini text-[var(--c-green-ink)]">My Notes</div>
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{w.client_notes}</p>
        </div>
      )}

      {stripDuration(w.description) && (
        <div className="mt-4 rounded-xl bg-[var(--c-film)] border border-[var(--c-hair)] p-3" data-testid={`workout-description-${assignment.id}`}>
          <div className="label-mini text-[#00C8FF]">Coach's Instructions</div>
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line leading-relaxed">{stripDuration(w.description)}</p>
        </div>
      )}

      {isCircuit && (
        <div className="mt-4 rounded-2xl border border-[#00C8FF]/25 bg-gradient-to-br from-[#00C8FF]/12 to-transparent p-4" data-testid={`circuit-summary-${assignment.id}`}>
          <div className="flex items-center gap-2 text-[#00C8FF]"><Repeat className="w-4 h-4" /><span className="label-mini text-[#00C8FF]">Circuit Workout</span></div>
          <div className="font-display font-black text-2xl mt-1">{rounds} {rounds === 1 ? 'ROUND' : 'ROUNDS'} · {items.length} {items.length === 1 ? 'EXERCISE' : 'EXERCISES'}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Recovery between exercises · {fmtDur(w.rest_between_rounds_seconds || 0)} between rounds
          </div>
        </div>
      )}

      {isWalking && (
        <div className="mt-4 regen-card p-4">
          <div className="flex items-center gap-3">
            <Footprints className="w-5 h-5 text-[#00C8FF]" />
            <div className="flex-1">
              <div className="label-mini">Steps</div>
              <Input type="number" value={steps} onChange={e => setSteps(e.target.value)} data-testid={`steps-input-${assignment.id}`} className="mt-1 bg-transparent border-[var(--c-hair)] font-mono text-lg" disabled={readOnly} />
            </div>
          </div>
        </div>
      )}

      {!isWalking && (
        <div className="mt-4 space-y-2">
          {(w.items || []).map((it, idx) => (
            <ExerciseRow key={idx} idx={idx} item={it} exercise={resolvedEx(idx, it)} showTempo={isStrength} substituted={!!subs[idx]} done={completed.has(String(idx))} onToggle={() => toggle(idx, it)} readOnly={readOnly || isCircuit} onOpenDetail={() => setDetailEx({ item: it, idx })} />
          ))}
        </div>
      )}

      {/* Preload next exercise video (360p) for instant playback */}
      {nextSrc && <video src={optimized360(nextSrc)} preload="auto" muted playsInline className="hidden" aria-hidden="true" data-testid="next-exercise-preload" />}

      {!readOnly && assignment.status !== 'completed' && (
        isCircuit ? (
          <Button onClick={() => setCircuitOpen(true)} data-testid={`start-circuit-${assignment.id}`} className="mt-4 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">
            <Play className="w-4 h-4 mr-2" /> Start Circuit
          </Button>
        ) : (
          <>
            {isRace && (
              <Button onClick={() => setRaceOpen(true)} data-testid={`start-race-${assignment.id}`} className="mt-4 w-full h-12 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">
                <Volume2 className="w-4 h-4 mr-2" /> Start voice-guided race
              </Button>
            )}
            {canGuide && (
              <Button onClick={() => setGuidedOpen(true)} data-testid={`start-guided-${assignment.id}`} className="mt-4 w-full h-12 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl">
                <Volume2 className="w-4 h-4 mr-2" /> Start voice-guided workout
              </Button>
            )}
            <Button onClick={complete} data-testid={`complete-workout-${assignment.id}`} className="mt-4 w-full bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726] font-semibold rounded-xl">
              <Check className="w-4 h-4 mr-2" /> Complete Workout
            </Button>
          </>
        )
      )}

      {canGuide && (
        <GuidedWorkout open={guidedOpen} onOpenChange={setGuidedOpen} workout={{ ...w, items: items.map((it, i) => ({ ...it, exercise_id: subs[i] || it.exercise_id })) }} exMap={exMap}
          onSave={async (payload) => {
            try {
              await api.post('/workouts/complete', { assignment_id: assignment.id, steps: null, guided: true, ...payload });
              toast.success('Workout saved — great session!'); onUpdate?.();
            } catch (e) { toast.error('Failed to save workout'); throw e; }
          }} />
      )}

      {(isCircuit || isRace) && (
        <GuidedWorkout open={isCircuit ? circuitOpen : raceOpen} onOpenChange={isCircuit ? setCircuitOpen : setRaceOpen} workout={w} exMap={exMap} kind={isCircuit ? 'circuit' : 'race'}
          onSave={async (payload) => {
            try {
              await api.post('/workouts/complete', { assignment_id: assignment.id, steps: null, guided: true, ...payload });
              toast.success(isCircuit ? 'Circuit complete — great work!' : 'Race complete — great work!'); onUpdate?.();
            } catch (e) { toast.error('Failed to save workout'); throw e; }
          }} />
      )}

      {/* Strength / standard recovery overlay — reuses the SAME RecoveryTimer as circuits. */}
      <Dialog open={!!restTimer} onOpenChange={(o) => !o && setRestTimer(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm p-6" data-testid="strength-recovery">
          <DialogHeader className="sr-only"><DialogTitle>Recovery</DialogTitle></DialogHeader>
          {restTimer && (
            <RecoveryTimer seconds={restTimer.seconds} nextLabel={restTimer.nextLabel} onDone={() => setRestTimer(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Exercise Detail Dialog */}
      <Dialog open={!!detailEx} onOpenChange={(o) => !o && setDetailEx(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[90vh] overflow-y-auto p-0">
          {detailEx && (() => {
            const origEx = exMap[detailEx.item.exercise_id];
            const detEx = exMap[subs[detailEx.idx] || detailEx.item.exercise_id];
            const isSubbed = !!subs[detailEx.idx];
            const alts = (origEx?.approved_alternatives || []).map(id => exMap[id]).filter(Boolean);
            return (
            <>
              <DialogHeader className="sr-only"><DialogTitle>{detEx?.name}</DialogTitle></DialogHeader>
              {detEx?.gif_url ? (
                <ExerciseMedia src={detEx.gif_url} alt={detEx.name} className="w-full aspect-video rounded-xl" fit="contain" />
              ) : <div className="w-full aspect-video bg-[var(--c-film)]" />}
              <div className="p-5">
                <div className="label-mini text-[#00C8FF]">{detEx?.category}</div>
                <div className="font-display font-black text-2xl mt-1">{detEx?.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{detEx?.muscle_group}</div>
                {isSubbed && (
                  <div className="mt-2 inline-flex items-center gap-1 pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-xs" data-testid="detail-subbed-badge">
                    <Repeat className="w-3 h-3" /> Swapped from {origEx?.name}
                  </div>
                )}

                <div className={`grid ${detailEx.item.weight ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mt-5`}>
                  <Stat label="Sets" value={detailEx.item.sets} />
                  <Stat label="Reps" value={detailEx.item.reps} />
                  {detailEx.item.weight && <Stat label="Weight" value={detailEx.item.weight} />}
                  <Stat label="Rest" value={`${detailEx.item.rest_seconds}s`} />
                </div>

                {!readOnly && !isCircuit && (
                  <div className="mt-5 grid grid-cols-2 gap-2" data-testid="detail-actuals">
                    <label className="block"><span className="label-mini">Reps done</span>
                      <Input value={actuals[detailEx.idx]?.reps_done || ''} placeholder={String(detailEx.item.reps)} data-testid="detail-reps-done"
                        onChange={e => setActuals(a => ({ ...a, [detailEx.idx]: { ...a[detailEx.idx], reps_done: e.target.value } }))} className="mt-1 bg-[var(--c-film)] border-[var(--c-hair)] font-mono" /></label>
                    <label className="block"><span className="label-mini">Weight used</span>
                      <Input value={actuals[detailEx.idx]?.weight_used || ''} placeholder={detailEx.item.weight || 'kg'} data-testid="detail-weight-used"
                        onChange={e => setActuals(a => ({ ...a, [detailEx.idx]: { ...a[detailEx.idx], weight_used: e.target.value } }))} className="mt-1 bg-[var(--c-film)] border-[var(--c-hair)] font-mono" /></label>
                  </div>
                )}

                {isStrength && (
                  <div className="mt-3 text-xs text-muted-foreground" data-testid="detail-tempo">Tempo: {resolveTempo(detailEx.item, detEx).label}</div>
                )}
                {detEx?.description && (
                  <div className="mt-5">
                    <div className="label-mini">How to Perform</div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{detEx.description}</p>
                  </div>
                )}
                {detailEx.item.notes && (
                  <div className="mt-4 p-3 rounded-xl bg-[#00C8FF]/5 border border-[#00C8FF]/20">
                    <div className="label-mini text-[#00C8FF]">Coach Notes</div>
                    <p className="text-sm mt-1">{detailEx.item.notes}</p>
                  </div>
                )}
                {detEx?.coach_notes && !detailEx.item.notes && (
                  <div className="mt-4 p-3 rounded-xl bg-[#00C8FF]/5 border border-[#00C8FF]/20">
                    <div className="label-mini text-[#00C8FF]">Coach Notes</div>
                    <p className="text-sm mt-1">{detEx.coach_notes}</p>
                  </div>
                )}

                {canSub && (alts.length > 0 || isSubbed) && (
                  <Button variant="outline" onClick={() => setSubPickIdx(detailEx.idx)} data-testid="detail-change-exercise"
                    className="mt-4 w-full h-11 rounded-xl border-[#00C8FF]/40 text-[#00C8FF] hover:bg-[#00C8FF]/10 font-semibold">
                    <Repeat className="w-4 h-4 mr-2" /> Change Exercise
                  </Button>
                )}

                {!readOnly && !isCircuit && (
                  <Button onClick={() => { toggle(detailEx.idx, detailEx.item); setDetailEx(null); }} data-testid="detail-toggle"
                    className={`mt-3 w-full h-12 rounded-xl font-semibold ${completed.has(String(detailEx.idx)) ? 'bg-[var(--c-film)]' : 'bg-[var(--c-green)] text-[#081726] hover:bg-[var(--c-green-soft)]'}`}>
                    {completed.has(String(detailEx.idx)) ? 'Unmark' : 'Mark Complete'}
                  </Button>
                )}
              </div>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Change Exercise picker — approved alternatives only (Strength) */}
      <Dialog open={subPickIdx !== null} onOpenChange={(o) => !o && setSubPickIdx(null)}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-sm max-h-[80vh] overflow-y-auto" data-testid="change-exercise-dialog">
          <DialogHeader><DialogTitle>Swap Exercise</DialogTitle></DialogHeader>
          {subPickIdx !== null && (() => {
            const it = items[subPickIdx];
            const origEx = exMap[it?.exercise_id];
            const alts = (origEx?.approved_alternatives || []).map(id => exMap[id]).filter(Boolean);
            const activeId = subs[subPickIdx] || it?.exercise_id;
            const pick = (exId) => {
              setSubs(prev => {
                const n = { ...prev };
                if (exId === it.exercise_id) delete n[subPickIdx]; else n[subPickIdx] = exId;
                return n;
              });
              setSubPickIdx(null);
            };
            return (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Keeps your coach's sets, reps, rest & tempo. This change applies to today's session only.</p>
                {[origEx, ...alts].filter(Boolean).map((ex, i) => (
                  <button key={ex.id} data-testid={`swap-option-${ex.id}`} onClick={() => pick(ex.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-xl border text-left transition ${activeId === ex.id ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-[var(--c-hair)] hover:bg-[var(--c-film)]'}`}>
                    {ex.gif_url ? <ExerciseMedia src={ex.gif_url} alt={ex.name} className="w-14 h-14 rounded-lg" fit="cover" /> : <div className="w-14 h-14 rounded-lg bg-[var(--c-film)]" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{ex.name} {i === 0 && <span className="text-[10px] text-muted-foreground">(coach's pick)</span>}</div>
                      <div className="text-[11px] text-muted-foreground">{ex.muscle_group || ex.category}</div>
                    </div>
                    {activeId === ex.id && <Check className="w-5 h-5 text-[#00C8FF]" />}
                  </button>
                ))}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SessionTools({ assignment, isCustom, onEdit, onUpdate }) {
  const restore = async () => {
    try { await api.delete(`/workouts/assignments/${assignment.id}/customize`); toast.success("Coach's original session restored"); onUpdate?.(); }
    catch { toast.error('Could not restore'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this workout you built?')) return;
    try { await api.delete(`/workouts/custom-session/${assignment.id}`); toast.success('Workout deleted'); onUpdate?.(); }
    catch { toast.error('Could not delete'); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-session-${assignment.id}`} className="border-[var(--c-hair)]">
        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit session
      </Button>
      {isCustom && (
        <Button size="sm" variant="outline" onClick={restore} data-testid={`restore-session-${assignment.id}`} className="border-[var(--c-hair)]">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore original
        </Button>
      )}
      {assignment.custom_built && (
        <Button size="sm" variant="outline" onClick={remove} data-testid={`delete-custom-${assignment.id}`} className="border-[var(--c-hair)] text-[#FF5C5C]">
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="regen-card p-3 text-center">
      <div className="label-mini">{label}</div>
      <div className="font-mono text-xl mt-1 text-[#00C8FF]">{value}</div>
    </div>
  );
}

function ExerciseRow({ item, exercise, done, onToggle, readOnly, onOpenDetail, idx = 0, substituted = false, showTempo = false }) {
  return (
    <div className="regen-card p-3 flex items-center gap-3">
      <button onClick={() => exercise && onOpenDetail(exercise)} data-testid={`open-ex-${idx}`} className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99] transition">
        {exercise?.gif_url ? (
          <ExerciseMedia src={exercise.gif_url} alt={exercise.name} className="w-16 h-16 rounded-lg" fit="cover" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-[var(--c-film)]" />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate flex items-center gap-1">
            {exercise?.name || item.exercise_name || 'Exercise'}
            {substituted && <Repeat className="w-3 h-3 text-[var(--c-green-ink)] shrink-0" data-testid={`subbed-${idx}`} />}
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono" data-testid={`ex-prescription-${idx}`}>{item.sets} × {item.reps}{item.weight ? ` @ ${item.weight}` : ''} • {item.rest_seconds}s rest</div>
          {showTempo && <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`ex-tempo-${idx}`}>Tempo {resolveTempo(item, exercise).label}{item.load_increase ? <span className="ml-1.5 text-[#00C8FF] font-semibold">· Heavier week</span> : null}</div>}
          {item.notes && <div className="text-xs text-[#00C8FF] mt-1 italic line-clamp-1">📝 {item.notes}</div>}
        </div>
      </button>
      {!readOnly && (
        <button onClick={onToggle} data-testid={`exercise-check-${idx}`}
          className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all active:scale-90 ${done ? 'bg-[var(--c-green)] border-[var(--c-green)]' : 'border-[var(--c-hair)]'}`}>
          {done && <Check className="w-5 h-5 text-[#081726]" strokeWidth={3} />}
        </button>
      )}
    </div>
  );
}


// human-readable duration for summaries (e.g. "4 min", "45s")
function fmtDur(s) {
  if (!s) return '0s';
  if (s % 60 === 0) return `${s / 60} min`;
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
